import "./style.scss";

import { Close } from "@mui/icons-material";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, CircularProgress, Drawer, Tab } from "@mui/material";
import ActionDownload from "assets/img/svg/actionDownload";
import ActionSee from "assets/img/svg/actionSee";
import IsOnline from "assets/img/svg/isOnline";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { IStatement } from "store/beneficiaryStore";
import { fetchPartnerStatements } from "store/partnerStatementsStore";
import { fetchPeriodExchangeRates } from "store/periodStore";
import {
    fetchAdvancePaymentsStatement,
    fetchAndSaveAdvanceStatement,
    fetchAndSaveStatement,
    fetchBeneficiaryNetStatement,
    fetchBeneficiaryPrecountStatement,
    fetchBeneficiaryStatement,
    fetchNetStatement,
    fetchPrecountStatement,
    fetchStatement,
    setStatementByBeneficiary,
} from "store/statementsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import EmptyState from "../EmptyState";
import { Spinner } from "../Spinner";
import StyledButton from "../StyledButton";
import { StyledTabs } from "../StyledTabs";
import { formatLocaleDateString, getCurrencyData, getStatementFilename } from "./../../../helpers/utils";
import CustomStatementBlock from "./CustomStatementBlock";

const statementTypes = [
    { labelKey: "Summary.StatementOfGrossDuties", type: "gross" },
    { labelKey: "Summary.StatementOfNetDuties", type: "net" },
    { labelKey: "Summary.PrepaymentCertification", type: "precount" },
    { labelKey: "Summary.Contract" },
];

const buttonStyle = {
    borderRadius: "4px",
    display: "flex",
    gap: "8px",
};

const whiteButtonStyle = {
    ...buttonStyle,
    "&.MuiButton-root": {
        borderWidth: "1px",
    },
};

const blackButtonStyle = {
    ...buttonStyle,
    "&.MuiButton-root": {
        borderWidth: "0",
        backgroundColor: "#2D2D2D",
        color: "#fefefe",
        "&:hover": {
            backgroundColor: "#4D4D4D",
        },
    },
};

interface IShowStatementsPublisher {
    isOpen: boolean;
    onCloseStatementDrawer: () => void;
    currentStatement: IStatement;
    beneficiary: IBeneficiar;
    isStatementCurrencyDefault: boolean;
    usesMultipleCurrencies: boolean;
    companyCurrency: string;
    activePeriod: number | undefined;
}

/**
 * Component that show statements on Statement Tab of Beneficiary page. Has similar design as what author sees on the portal.
 */
const ShowStatementsPublisher = ({
    isOpen,
    onCloseStatementDrawer,
    currentStatement,
    beneficiary,
    isStatementCurrencyDefault,
    usesMultipleCurrencies,
    companyCurrency,
    activePeriod,
}: IShowStatementsPublisher) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { companyInfos, getCompanySettings, countryFeatures } = useCompanySettings();
    const usesStatementTemplates = getCompanySettings("uses_custom_statement_templates");
    const usesMarketSegmentation = getCompanySettings("uses_market_segmentation");
    const { showNet, showPrecount } = countryFeatures.statementBlocks;
    const [currentTab, setCurrentTab] = useState(0);
    const statementContentRef = useRef<any>(null);
    const {
        status,
        statement,
        statementByBeneficiary,
        netStatement,
        netStatementByBeneficiary,
        advancePaymentNetStatement,
        precountStatement,
        precountStatementByBeneficiary,
        advancePaymentPrecountStatement,
        savingStatus,
    } = useSelector((state: RootState) => state.statements);
    const statementPdfRef = useRef<any>(null);
    const partnerStatements = useAppSelector((state) => state.partnerStatements.partnerStatements);
    const isCustomStatement = !!beneficiary?.customStatementTemplateId && currentStatement?.type === "consolidated";
    const [showCustomStatement, setShowCustomStatement] = useState(false);

    const beneficiaryCustomStatement = useMemo(
        () => partnerStatements.find((statement) => statement.id == beneficiary?.customStatementTemplateId),
        [beneficiary],
    );

    const { periodExchangeRates } = useAppSelector((state) => state.period);

    useEffect(() => {
        activePeriod && dispatch(fetchPeriodExchangeRates({ periodId: activePeriod }));
    }, [activePeriod]);

    useEffect(() => {
        if (isCustomStatement && !partnerStatements.length) dispatch(fetchPartnerStatements());
    }, [isCustomStatement]);

    /**
     * Reset initial statementByBeneficiary when changing beneficiary;
     */
    useEffect(() => {
        if (usesStatementTemplates && currentStatement.type === "consolidated") {
            dispatch(setStatementByBeneficiary(""));
            setShowCustomStatement(false);
        }
    }, [beneficiary, currentStatement]);

    useEffect(() => {
        if (currentStatement && isOpen) {
            if (currentStatement.type === "title") {
                currentTab == 0 &&
                    currentStatement.termId &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchStatement({
                            termId: currentStatement.termId,
                            periodId: currentStatement.periodId,
                        }),
                    );
                currentTab == 1 &&
                    currentStatement.termId &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchNetStatement({
                            termId: currentStatement.termId,
                            periodId: currentStatement.periodId,
                        }),
                    );
                currentTab == 2 &&
                    currentStatement.termId &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchPrecountStatement({
                            termId: currentStatement.termId,
                            periodId: currentStatement.periodId,
                        }),
                    );
            } else if (currentStatement.type === "consolidated") {
                currentTab == 0 &&
                    !isCustomStatement &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchBeneficiaryStatement({
                            beneficiaryId: beneficiary.id,
                            periodId: currentStatement.periodId,
                        }),
                    );
                currentTab == 0 &&
                    isCustomStatement &&
                    showCustomStatement &&
                    !statementByBeneficiary &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchBeneficiaryStatement({
                            beneficiaryId: beneficiary.id,
                            periodId: currentStatement.periodId,
                        }),
                    );
                currentTab == 1 &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchBeneficiaryNetStatement({
                            beneficiaryId: beneficiary.id,
                            periodId: currentStatement.periodId,
                        }),
                    );
                currentTab == 2 &&
                    currentStatement.periodId &&
                    dispatch(
                        fetchBeneficiaryPrecountStatement({
                            beneficiaryId: beneficiary.id,
                            periodId: currentStatement.periodId,
                        }),
                    );
            } else if (currentStatement.type === "advance" && currentStatement.termId && currentStatement.sliceId) {
                currentTab == 0 &&
                    dispatch(
                        fetchAdvancePaymentsStatement({
                            termId: currentStatement.termId,
                            advancePaymentId: currentStatement.id,
                            advancePaymentSliceId: currentStatement.sliceId,
                            statementType: "net",
                        }),
                    );
                currentTab == 1 &&
                    dispatch(
                        fetchAdvancePaymentsStatement({
                            termId: currentStatement.termId,
                            advancePaymentId: currentStatement.id,
                            advancePaymentSliceId: currentStatement.sliceId,
                            statementType: "precount",
                        }),
                    );
            }
        }
    }, [dispatch, currentStatement, beneficiary, currentTab, isCustomStatement, showCustomStatement]);

    const savePdf = useCallback(() => {
        if (savingStatus == "pending" || !companyInfos) return;
        const { id: companyId } = companyInfos;
        const { id: beneficiarId } = beneficiary;
        const { name: statementName, termId, periodId, sliceId: advancePaymentSliceId } = currentStatement;

        const beneficiaryFullName = `${beneficiary.surname} ${beneficiary.name ?? ""}`;
        let statementType = statementTypes[currentTab].type;
        let statementTypeLabel = t(statementTypes[currentTab].labelKey);

        switch (currentStatement.type) {
            case "title":
                {
                    if (!periodId) return;
                    const fileName = getStatementFilename(
                        beneficiaryFullName,
                        statementName,
                        statementTypeLabel,
                        new Date(),
                    );
                    dispatch(fetchAndSaveStatement({ termId, periodId, fileName, statementType, companyId }));
                }
                break;
            // Consolidated Statement doesn't have termId
            case "consolidated":
                {
                    if (!periodId) return;
                    const fileName = getStatementFilename(beneficiaryFullName, "", statementTypeLabel, new Date());
                    dispatch(fetchAndSaveStatement({ beneficiarId, periodId, fileName, statementType, companyId }));
                }
                break;

            case "advance": {
                if (!termId || !advancePaymentSliceId) return;
                statementTypeLabel = t("StatementsTab.AdvanceStatement");
                statementType = statementTypes[currentTab + 1].type;
                const fileName = getStatementFilename(
                    beneficiaryFullName,
                    statementName,
                    statementTypeLabel,
                    new Date(),
                );
                dispatch(
                    fetchAndSaveAdvanceStatement({
                        termId,
                        advancePaymentId: currentStatement.id,
                        advancePaymentSliceId,
                        fileName,
                        statementType,
                        companyId,
                    }),
                );
            }
        }
    }, [dispatch, savingStatus, currentTab, beneficiary, currentStatement]);

    const renderStatusChip = (status: number) => {
        if (status == 0)
            return (
                <div className="bpstatement-period-status closed">
                    <IsOnline fill="#267847" />
                    {t("BPStatement.ClosedPeriod")}
                </div>
            );
        if (status == 1)
            return (
                <div className="bpstatement-period-status opened">
                    <IsOnline fill="#848B4F" />
                    {t("Management.OpenedPeriod")}
                </div>
            );
        if (status == 2)
            return (
                <div className="bpstatement-period-status pending">
                    <IsOnline fill="#9185CB" />
                    {t("Management.FuturePeriod")}
                </div>
            );
        return;
    };

    return (
        <>
            <Drawer
                anchor={"right"}
                className="show-statement-component"
                open={isOpen}
                onClose={onCloseStatementDrawer}
            >
                <div className="show-statements" ref={statementContentRef}>
                    <div className="show-statements-header">
                        <div className="show-statement-title">
                            <div className="show-statement-title-avatar">
                                <Avatar
                                    variant="rounded"
                                    src={currentStatement?.avatarUrl ?? "#"}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        backgroundColor: "#FFF2F1",
                                        color: "black",
                                        fontSize: 12,
                                    }}
                                    imgProps={{ sx: { objectFit: "cover" } }}
                                >
                                    {currentStatement?.name?.slice(0, 1).toUpperCase()}
                                </Avatar>
                                <span>{currentStatement?.name}</span>
                            </div>
                            <Close
                                style={{ color: variables["foreground"], cursor: "pointer" }}
                                onClick={onCloseStatementDrawer}
                            />
                        </div>
                        <div className="show-statement-subtitle">
                            {currentStatement &&
                                ["title", "consolidated"].includes(currentStatement?.type) &&
                                t("ShowStatements.StatementOf", {
                                    from: formatLocaleDateString({ date: currentStatement.periodStart }),
                                    to: formatLocaleDateString({ date: currentStatement.periodEnd }),
                                })}
                            {renderStatusChip(currentStatement?.isPeriodActive)}
                        </div>
                        {usesMultipleCurrencies && !isStatementCurrencyDefault && (
                            <div className="summary-exchange-rate">
                                <div>
                                    <span>{t("Summary.ExchangeRate")}</span>
                                </div>
                                <div className="exchange-rate-details">
                                    <div className="rates">
                                        <span>1 {companyCurrency ?? "EUR"}</span>
                                        <img src={getCurrencyData(companyCurrency ?? "EUR")?.flag} />
                                    </div>
                                    <SwapHorizOutlinedIcon
                                        style={{
                                            color: variables["foreground"],
                                            height: "20px",
                                            width: "20px",
                                            opacity: "0.7",
                                        }}
                                    />
                                    <div className="rates">
                                        <span>
                                            {
                                                periodExchangeRates.find(
                                                    (rate) => rate.currency == (beneficiary.statementCurrency ?? "EUR"),
                                                )?.rate
                                            }{" "}
                                            {beneficiary.statementCurrency}
                                        </span>
                                        <img src={getCurrencyData(beneficiary.statementCurrency ?? "EUR")?.flag} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="show-statements-main">
                        {!isCustomStatement && (
                            <div className="show-statement-actions">
                                {(currentStatement?.workId || currentStatement?.titleId) && (
                                    <StyledButton
                                        sx={whiteButtonStyle}
                                        onClick={() =>
                                            navigate(
                                                currentStatement.workId
                                                    ? `/works/${currentStatement.workId}`
                                                    : `/titles/${currentStatement.titleId}`,
                                            )
                                        }
                                    >
                                        <ActionSee className="show-statement-actions-svg" />
                                        {t(currentStatement.workId ? "Works.SeeWork" : "ShowStatements.SeeTitle")}
                                    </StyledButton>
                                )}
                                <StyledButton
                                    sx={blackButtonStyle}
                                    onClick={() => savePdf()}
                                    disabled={savingStatus == "pending"}
                                >
                                    {savingStatus == "pending" ? (
                                        <CircularProgress size={24} sx={{ color: "#fefefe" }} />
                                    ) : (
                                        <>
                                            <ActionDownload className="show-statement-actions-svg" />
                                            {t("ShowStatements.Download")}
                                        </>
                                    )}
                                </StyledButton>
                            </div>
                        )}
                        <TabContext value={currentTab.toString()}>
                            <StyledTabs value={+currentTab} onChange={(_, tab) => setCurrentTab(tab)}>
                                {currentStatement && currentStatement.type !== "advance" && (
                                    <Tab label={t("ShowStatements.GrossDuties")} />
                                )}
                                {showNet && (
                                    <Tab label={t("ShowStatements.NetDuties")} disabled={!isStatementCurrencyDefault} />
                                )}
                                {showPrecount && (
                                    <Tab
                                        label={t("ShowStatements.Prepayment")}
                                        disabled={currentStatement?.precount == "0" || !isStatementCurrencyDefault}
                                    />
                                )}
                            </StyledTabs>

                            {currentStatement && currentStatement.type !== "advance" && (
                                <TabPanel sx={{ padding: 0 }} value="0">
                                    {isCustomStatement && (
                                        <CustomStatementBlock
                                            showCustomStatement={showCustomStatement}
                                            statementByBeneficiary={statementByBeneficiary}
                                            beneficiaryCustomStatement={beneficiaryCustomStatement}
                                            status={status}
                                            onShowCustomStatement={() => setShowCustomStatement(true)}
                                        />
                                    )}
                                    {!isCustomStatement && (
                                        <>
                                            {status === "pending" && (
                                                <div className="spinner-wrapper">
                                                    <Spinner />
                                                    <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                                </div>
                                            )}
                                            {status === "failed" && (
                                                <div className="spinner-wrapper" style={{ height: "unset" }}>
                                                    {usesMarketSegmentation ? (
                                                        <div className="no-statement-info">
                                                            <span className="message">
                                                                {t("Summary.MarketSegmentationInfo")}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <EmptyState type="statementFailed" />
                                                    )}
                                                </div>
                                            )}
                                            {status === "succeeded" && (
                                                <div
                                                    style={{ maxWidth: "960px" }}
                                                    ref={statementPdfRef}
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            currentStatement && currentStatement.type === "consolidated"
                                                                ? statementByBeneficiary
                                                                : statement,
                                                    }}
                                                ></div>
                                            )}
                                            {status === "idle" && (
                                                <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                            )}
                                        </>
                                    )}
                                </TabPanel>
                            )}

                            {showNet && (
                                <TabPanel
                                    sx={{ padding: 0 }}
                                    value={currentStatement && currentStatement.type === "advance" ? "0" : "1"}
                                >
                                    {status === "pending" && (
                                        <div className="spinner-wrapper">
                                            <Spinner />
                                            <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                        </div>
                                    )}
                                    {status === "failed" && (
                                        <div className="spinner-wrapper" style={{ height: "unset" }}>
                                            {usesMarketSegmentation ? (
                                                <div className="no-statement-info">
                                                    <span className="message">
                                                        {t("Summary.MarketSegmentationInfo")}
                                                    </span>
                                                </div>
                                            ) : (
                                                <EmptyState type="statementFailed" />
                                            )}
                                        </div>
                                    )}
                                    {status === "succeeded" && (
                                        <div
                                            style={{ maxWidth: "960px" }}
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    currentStatement && currentStatement.type === "consolidated"
                                                        ? netStatementByBeneficiary
                                                        : currentStatement && currentStatement.type === "advance"
                                                          ? advancePaymentNetStatement
                                                          : netStatement,
                                            }}
                                        ></div>
                                    )}
                                    {status === "idle" && (
                                        <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                    )}
                                </TabPanel>
                            )}
                            {showPrecount && (
                                <TabPanel
                                    sx={{ padding: 0 }}
                                    value={currentStatement && currentStatement.type === "advance" ? "1" : "2"}
                                >
                                    {status === "pending" && (
                                        <div className="spinner-wrapper">
                                            <Spinner />
                                            <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                        </div>
                                    )}
                                    {status === "failed" && (
                                        <div className="spinner-wrapper" style={{ height: "unset" }}>
                                            {usesMarketSegmentation ? (
                                                <div className="no-statement-info">
                                                    <span className="message">
                                                        {t("Summary.MarketSegmentationInfoPrecount")}
                                                    </span>
                                                </div>
                                            ) : (
                                                <EmptyState type="statementFailed" />
                                            )}
                                        </div>
                                    )}
                                    {status === "succeeded" && (
                                        <div
                                            style={{ maxWidth: "960px" }}
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    currentStatement && currentStatement.type === "consolidated"
                                                        ? precountStatementByBeneficiary
                                                        : currentStatement && currentStatement.type === "advance"
                                                          ? advancePaymentPrecountStatement
                                                          : precountStatement,
                                            }}
                                        ></div>
                                    )}
                                    {status === "idle" && (
                                        <div style={{ textAlign: "center" }}>{t("ShowStatements.NoData")}</div>
                                    )}
                                </TabPanel>
                            )}
                        </TabContext>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default ShowStatementsPublisher;
